<template>
  <div class="w-full vx-row no-gutter waiting-list-form waiting-list-form--full privacy-policy-form">
    <hosted-page-banner
      :brand-name="brandNameLocal"
      :brand-colour="brandColourLocal"
      :icon-path="iconPathLocal"
      :logo-path="logoPathLocal"
      :prefer-logo-over-icon="preferLogoOverIconLocal"
    >
      <h3 class="text-center text-inherit w-full mt-5 lg:text-3xl">
        {{ titleText }}
      </h3>
    </hosted-page-banner>

    <div class="vx-col w-full bg-white p-base md:flex justify-center">
      <div
        class="w-full lg:w-2/3 xl:w-1/2 ql-editor"
        v-html="processedHtmlPolicy">
      </div>
    </div>

    <!-- GrapePerks Disclaimer Footer -->
    <div class="vx-col w-full bg-gray-100 p-base md:flex justify-center">
      <div class="w-full lg:w-2/3 xl:w-1/2">
        <div class="text-xs text-gray-600 border-t pt-4">
          <p class="mb-1">
            This privacy policy is hosted by GrapePerks on behalf of the business above. GrapePerks is not responsible for the privacy or security practices of its customers.
          </p>
          <p>
            Never share sensitive personal information unless you trust the business you're interacting with.
          </p>
          <p class="mt-2">
            For GrapePerks platform policies, see our
            <a
              :href="legalUrlWithTracking"
              target="_blank"
              rel="noopener noreferrer"
              class="gp_simple-link"
            >legal terms and policies</a>.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import waitingListService from '@/api/modules/waiting-list.service';
import themeConfig from '../../../themeConfig';
import { getContrastColor } from '@/utils/colorUtils';
import HostedPageBanner from '@/components/HostedPageBanner.vue';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.core.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.snow.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.bubble.css';
import allCountries from '@/assets/utils/all-countries';

/**
 * Page of auth, wrap the login and register components
 *
 * @module views/pages/ContactOptOut
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'HostedPrivacyPolicy',
  components: {
    HostedPageBanner,
  },
  i18n: {
    messages: {
      en: {
        Title: 'Privacy Policy',
      },
    },
  },
  props: {
    brandName: {
      type: String,
      default: 'Company Name',
    },
    brandColour: {
      type: String,
      default: themeConfig.colors.primary,
    },
    accentColour: {
      type: String,
      default: themeConfig.colors.primary,
    },
    iconPath: {
      type: String,
      default: '',
    },
    logoPath: {
      type: String,
      default: '',
    },
    preferLogoOverIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNational: '',
      codeCountry: '',

      phoneValidate: { isValid: false },
      phoneNumberInfo: {},
      phoneInput: '',
      optInAgreement: false,

      brandNameLocal: this.brandName,
      iconPathLocal: this.iconPath,
      logoPathLocal: this.logoPath,
      preferLogoOverIconLocal: this.preferLogoOverIcon,
      brandColourLocal: this.brandColour,
      accentColourLocal: this.accentColour,

      policyType: '',
      inlinePolicy: '',
      linkPolicy: '',
      generatedPolicy: '',

      // Privacy policy placeholder data (fetched from API, not from Vuex)
      reviewLink: '',
      accountDetails: null,
    };
  },
  computed: {
    htmlPolicy() {
      return this.policyType === this.$enums.Tenants.POLICY_TYPE.INLINE
        ? this.inlinePolicy
        : this.generatedPolicy;
    },
    getCountryFullName() {
      if (!this.accountDetails.country) return 'COUNTRY';

      const country = allCountries.find(
        (c) => c.iso2 === this.accountDetails.country.toUpperCase(),
      );
      return country ? country.name : this.accountDetails.country;
    },
    processedHtmlPolicy() {
      if (!this.htmlPolicy) return '';

      // Create a temporary DOM element to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.htmlPolicy;

      // Find all placeholder spans
      const placeholders = tempDiv.querySelectorAll('span.placeholder');

      placeholders.forEach((placeholder) => {
        let attribute = placeholder.getAttribute('data-attribute');
        const placeholderName = placeholder.getAttribute('data-name');
        let replacementValue = '';

        // If no data-attribute, try to map from data-name to attribute
        if ((!attribute || attribute === 'undefined') && placeholderName) {
          // Map placeholder name to attribute using enums
          const businessPlaceholderEntry = Object.entries(this.$enums.Campaign.BusinessPlaceholders)
            .find(([key, label]) => label === placeholderName);

          if (businessPlaceholderEntry) {
            const [key] = businessPlaceholderEntry;
            attribute = this.$enums.Campaign.BusinessPlaceholdersAttributeName[key];
          } else {
            // Check if it's a special placeholder (like Review Link)
            const specialPlaceholders = Object.values(this.$enums.Campaign.SpecialPlaceholders);
            if (specialPlaceholders.includes(placeholderName)) {
              attribute = placeholderName;
            }
          }
        }

        // Get replacement value based on attribute
        if (attribute === this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK) {
          replacementValue = this.reviewLink || '';
        } else if (attribute
          && this.accountDetails
          && attribute === this.$enums.Campaign.BusinessPlaceholdersAttributeName.BUSINESS_COUNTRY) {
          replacementValue = this.getCountryFullName;
        } else if (attribute && this.accountDetails) {
          // It's a business details placeholder
          // Navigate through nested properties using the attribute path
          replacementValue = attribute.split('.').reduce((obj, key) => obj?.[key], this.accountDetails) || '';
        }

        // Create replacement element (link or text node)
        let replacementElement;

        // Special handling for links
        if (attribute === this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK && replacementValue) {
          // Review link - create anchor element
          replacementElement = document.createElement('a');
          replacementElement.href = replacementValue;
          replacementElement.textContent = replacementValue;
          replacementElement.target = '_blank';
          replacementElement.rel = 'noopener noreferrer';
        } else if (attribute === this.$enums.Campaign.BusinessPlaceholdersAttributeName.BUSINESS_WEBSITE && replacementValue) {
          // Business website - create anchor element
          replacementElement = document.createElement('a');
          replacementElement.href = replacementValue;
          replacementElement.textContent = replacementValue;
          replacementElement.target = '_blank';
          replacementElement.rel = 'noopener noreferrer';
        } else if (attribute === this.$enums.Campaign.BusinessPlaceholdersAttributeName.BUSINESS_EMAIL && replacementValue) {
          // Business email - create mailto link
          replacementElement = document.createElement('a');
          replacementElement.href = `mailto:${replacementValue}`;
          replacementElement.textContent = replacementValue;
        } else {
          // Default: text node
          replacementElement = document.createTextNode(replacementValue);
        }

        // Replace the placeholder span with the replacement element
        placeholder.replaceWith(replacementElement);
      });

      return tempDiv.innerHTML;
    },
    titleText() {
      return String(this.$t('Title'));
    },
    accentContrastTextColor() {
      return this.accentColourLocal ? this.getContrastColor(this.accentColourLocal) : '';
    },
    legalUrlWithTracking() {
      const baseUrl = this.$enums.AppLinks.LEGAL;
      const params = new URLSearchParams({
        source: 'hosted-privacy-policy',
        element: 'legal-footer-link',
      });

      return `${baseUrl}?${params.toString()}`;
    },
  },
  watch: {
    brandColour(val) {
      this.brandColourLocal = val;
    },
    accentColour(val) {
      this.accentColourLocal = val;
    },
    iconPath(val) {
      this.iconPathLocal = val;
    },
    logoPath(val) {
      this.logoPathLocal = val;
    },
    preferLogoOverIcon(val) {
      this.preferLogoOverIconLocal = val;
    },
  },
  created() {
    // Add SEO meta tags to prevent search engine indexing
    this.addNoIndexMetaTags();

    const { tenantUrlLinksSuffix } = this.$route.params;

    if (tenantUrlLinksSuffix) {
      this.fetchWaitingListDetails(tenantUrlLinksSuffix);
    } else {
      this.$router.push({ name: 'not-authorized' });
    }
  },
  methods: {
    addNoIndexMetaTags() {
      // Remove existing meta tags if they exist
      const existingRobotsTag = document.querySelector('meta[name="robots"]');
      const existingGooglebotTag = document.querySelector('meta[name="googlebot"]');

      if (existingRobotsTag) {
        existingRobotsTag.remove();
      }
      if (existingGooglebotTag) {
        existingGooglebotTag.remove();
      }

      // Add noindex meta tags
      const robotsTag = document.createElement('meta');
      robotsTag.name = 'robots';
      robotsTag.content = 'noindex, nofollow';
      document.head.appendChild(robotsTag);

      const googlebotTag = document.createElement('meta');
      googlebotTag.name = 'googlebot';
      googlebotTag.content = 'noindex, nofollow';
      document.head.appendChild(googlebotTag);
    },
    async fetchWaitingListDetails(tenantUrlLinksSuffix) {
      this.$vs.loading({ type: 'radius' });

      try {
        const resp = await waitingListService.getWaitingListBrandingDetails(tenantUrlLinksSuffix);
        const {
          name,
          icon,
          logo,
          preferLogoOverIcon,
          brandColour,
          accentColor,
          policyType,
          inlinePolicy,
          generatedPolicy,
          linkPolicy,
          reviewLink,
          accountDetails,
        } = resp.data;

        this.brandNameLocal = name;
        this.iconPathLocal = icon;
        this.logoPathLocal = logo;
        this.preferLogoOverIconLocal = preferLogoOverIcon;
        this.brandColourLocal = brandColour;
        this.accentColourLocal = accentColor;

        this.policyType = policyType;
        this.inlinePolicy = inlinePolicy;
        this.generatedPolicy = generatedPolicy;
        this.linkPolicy = linkPolicy;

        // Store placeholder data for privacy policy processing
        this.reviewLink = reviewLink;
        this.accountDetails = accountDetails;
      } catch (err) {
        // Invalid tenant - redirect to 404 page
        this.$router.push({ name: 'page-error-404' });
      } finally {
        this.$vs.loading.close();
      }
    },
    getContrastColor,
  },
};
</script>

<style lang="scss">
.waiting-list-form {
  &.waiting-list-form--full {
    // Use viewport helper variable with fallback
    min-height: 100vh;
    min-height: calc(var(--vh, 1vh) * 100);
    position: relative;

    @supports (-webkit-touch-callout: none) {
      min-height: -webkit-fill-available;
    }
  }

  .vs-checkbox--check {
    background: v-bind('accentColourLocal') !important;
    @apply shadow-md;
  }

  .logo-responsive {
    max-height: 55px !important;
    max-width: 120px !important;

    @media (min-width: 768px) {
      max-height: 80px !important;
      max-width: 200px !important;
    }
  }

  .con-vs-avatar {
    @apply bg-white;
  }
}
</style>
